import { timestampDate, timestampFromDate } from '@bufbuild/protobuf/wkt'
import _ from 'lodash'
import { CalendarIcon, CircleAlertIcon, Loader2Icon } from 'lucide-react'
import { useMemo, useState } from 'react'

import { useListCompanyArtifact } from '@/api/artifact.hook'
import { useFinalizeAssessment, useListOngoingAssessments } from '@/api/assessments.hook'
import {
  useGetCompany,
  useUpdateCompany,
  useUpdateOrgCompanyReAssessmentDueDate,
} from '@/api/company.hook'
import { Artifact } from '@/gen/artifacts/v1/model_pb'
import { AssessmentDecision } from '@/gen/inventory/v1/assessment_decision_pb'
import { ReAssessmentDueDateUpdateType } from '@/gen/inventory/v1/company_service_pb'
import { CompanyStatus } from '@/gen/inventory/v1/company_status_pb'
import { OngoingAssessmentStatus } from '@/gen/inventory/v1/ongoing_assessment_pb'
import { Project } from '@/gen/inventory/v1/project_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { WARNING_EXPIRY_DAYS } from '@/const/artifact-expiry'
import {
  assessmentDecisionLabel,
  companyStatusLabel,
  controlStatusLabel,
  riskLevelLabel,
} from '@/const/label'

import { EventProperties, useTrackCallback } from '@/lib/analytics/events'
import { useReportErrorsCallback } from '@/lib/connect/error-reporting'
import { MILLISECONDS_IN_A_DAY, formatDate, formatTimestamp } from '@/lib/date/date'

import { ASSESSMENT_EXPORT_SOURCES, useAssessmentAnalytics } from '@/pages/assessment/analytics'
import { useIsIrqCompleted } from '@/pages/assessment/irq/use-is-irq-completed'
import { useControlCountStats } from '@/pages/assessment/use-control-count-stats'
import { useIsAllControlValidated } from '@/pages/assessment/use-is-all-control-validated'
import { ExportCompany } from '@/pages/company-drawer/components/export-company'

import { Button } from '@/components/lemmala-design-system/button/button'
import { DatePicker } from '@/components/lemmala-design-system/date-picker/date-picker'
import { FormControl } from '@/components/lemmala-design-system/form-control/form-control'
import { Modal } from '@/components/lemmala-design-system/modal/modal'
import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/lemmala-design-system/radio-group/radio-group'
import { useToast } from '@/components/lemmala-design-system/toast/use-toast'
import { AssessmentDecisionBadge } from '@/components/shared/assessment-decision-badge/assessment-decision-badge'
import { getInherentRisk } from '@/components/shared/risk-badge/utils'

import { FINISH_ASSESSMENT_MODAL_LABELS } from './labels'

type DueDateOption = 'calculated' | 'existing' | 'custom'

export type FinishAssessmentModalProps = {
  open: boolean
  onClose: () => void
  decision: AssessmentDecision
  companyId: string
  assessmentId?: string
  projectId?: Project['id']
  extraTrackingProps?: EventProperties
  comment?: string
  onFinishAssessment?: (
    hasRejectedAssessment: boolean,
    isFinalAssessment: boolean,
    pastAssessmentId?: string,
  ) => void
}

export const FinishAssessmentModal = ({
  open,
  onClose,
  decision,
  companyId,
  assessmentId,
  projectId: projectIdProp,
  extraTrackingProps,
  comment = '',
  onFinishAssessment,
}: FinishAssessmentModalProps) => {
  const controlCountStats = useControlCountStats(assessmentId)
  const trackingContext = useAssessmentAnalytics()
  const projectId = projectIdProp ?? trackingContext?.projectId
  const { mutateAsync: updateCompany, isPending: isUpdatingCompany } = useUpdateCompany(companyId)
  const { data: company } = useGetCompany(companyId)
  const reportErrors = useReportErrorsCallback()
  const { toast } = useToast()
  const { mutateAsync: finalizeAssessment, isPending } = useFinalizeAssessment()
  const { data: ongoingAssessments } = useListOngoingAssessments(companyId)
  const { mutateAsync: updateDueDate, isPending: isUpdatingDueDate } =
    useUpdateOrgCompanyReAssessmentDueDate(companyId)
  const trackStatusUpdate = useTrackCallback('third-party.status.update')
  const trackFinishAssessment = useTrackCallback('third-party.assessment.finish')
  const trackManualDueDate = useTrackCallback('third-party.reassessment-due-date.manual-set')

  const [dueDateOption, setDueDateOption] = useState<DueDateOption>('calculated')
  const [customDueDate, setCustomDueDate] = useState<Date | undefined>(undefined)

  // Calculate default next due date based on cadence (today + cadence)
  const cadenceSeconds = company?.company?.reAssessmentDueDate?.cadence?.seconds
  const calculatedNextDueDate = useMemo(() => {
    if (!cadenceSeconds) return undefined
    const now = new Date().getTime()
    return new Date(now + Number(cadenceSeconds) * 1000)
  }, [cadenceSeconds])

  // Get existing scheduled due date from company data
  const existingScheduledDueDate = useMemo(() => {
    const dueDate = company?.company?.reAssessmentDueDate?.dueDate
    return dueDate ? timestampDate(dueDate) : undefined
  }, [company?.company?.reAssessmentDueDate?.dueDate])

  const warningDate = useMemo(() => {
    return new Date().getTime() + WARNING_EXPIRY_DAYS * MILLISECONDS_IN_A_DAY
  }, [])
  const isAllControlsValidated = useIsAllControlValidated(assessmentId)
  const engagementModeEnabled = !!company?.company?.engagementMode
  const isIrqCompleted = useIsIrqCompleted(companyId, engagementModeEnabled)

  const { data: artifactsData } = useListCompanyArtifact(companyId)
  const artifactPassWarningTime = _.chain(artifactsData?.artifacts || [])
    .filter((artifact) => !!artifact.expirationDate)
    .groupBy('artifactSubcategory')
    .map((artifacts) => {
      return _.maxBy(artifacts, (artifact) =>
        artifact.expirationDate ? timestampDate(artifact.expirationDate).getTime() : -Infinity,
      )
    })
    .filter(
      (artifact) =>
        !!artifact &&
        !!artifact.expirationDate &&
        artifact.expirationDate &&
        timestampDate(artifact.expirationDate).getTime() < warningDate,
    )
    .value() as Artifact[]

  const alerts = [
    {
      condition: !isIrqCompleted,
      message: FINISH_ASSESSMENT_MODAL_LABELS.IRQ_NOT_COMPLETED,
    },
    ...artifactPassWarningTime
      .filter((artifact) => !!artifact.expirationDate)
      .map(({ id, expirationDate, artifactSubcategory }) => {
        if (!expirationDate) return null
        const expirationDateTime = timestampDate(expirationDate).getTime()
        const currentTime = new Date().getTime()
        const isExpired = expirationDateTime < currentTime
        const formattedDate = formatTimestamp(expirationDate) ?? ''
        const subcategory = artifactSubcategory ?? ''
        return {
          condition: true,
          id,
          message: isExpired
            ? FINISH_ASSESSMENT_MODAL_LABELS.ARTIFACT_EXPIRED(subcategory, formattedDate)
            : FINISH_ASSESSMENT_MODAL_LABELS.ARTIFACT_EXPIRES(subcategory, formattedDate),
        }
      }),
    {
      condition: !isAllControlsValidated,
      message: FINISH_ASSESSMENT_MODAL_LABELS.CONTROLS_NOT_VALIDATED,
    },
  ]

  const hasAlerts = alerts.some((alert) => alert?.condition)

  // Only validate custom date when the custom option is selected
  const isCustomDateInvalid = dueDateOption === 'custom' && !customDueDate

  const trackAssessmentCompletion = () => {
    trackStatusUpdate({
      companyId,
      status: companyStatusLabel[CompanyStatus.APPROVED],
      previousStatus: companyStatusLabel[company?.company?.status ?? CompanyStatus.UNSPECIFIED],
      comment,
      company: company?.company?.profile?.name,
      projectId,
      ...extraTrackingProps,
    })
    trackFinishAssessment({
      assessmentId,
      companyId,
      company: company?.company?.profile?.name,
      decision: assessmentDecisionLabel[decision],
      comment,
      projectId,

      controlCountStats: _.fromPairs(
        controlCountStats.map(({ status, count }) => [controlStatusLabel[status], count]),
      ),
      alertCountStats: company?.company?.alertsByLevel.reduce(
        (acc, { riskLevel, count }) => {
          acc[riskLevelLabel[riskLevel]] = count
          return acc
        },
        {
          [riskLevelLabel[RiskLevel.CRITICAL]]: 0,
          [riskLevelLabel[RiskLevel.HIGH]]: 0,
          [riskLevelLabel[RiskLevel.MEDIUM]]: 0,
          [riskLevelLabel[RiskLevel.LOW]]: 0,
        } as Record<string, number>,
      ),

      openIssues: {
        isIrqCompleted,
        hasExpiredArtifacts: artifactPassWarningTime.length > 0,
        isAllControlsValidated,
      },
      dueDateOption,
      customDueDate: dueDateOption === 'custom' && customDueDate ? formatDate(customDueDate) : null,
      existingScheduledDueDate: existingScheduledDueDate
        ? formatDate(existingScheduledDueDate)
        : null,
      ...extraTrackingProps,
    })
  }

  const finalizeAssessmentsAndUpdateStatus = async () => {
    const activeOngoingAssessments = ongoingAssessments?.ongoingAssessments?.filter(
      (assessment) => !assessment.pastAssessmentId,
    )

    if (assessmentId) {
      const result = await finalizeAssessment({
        assessmentId,
        companyId,
        decision,
      })
      onFinishAssessment?.(
        result.hasRejectedAssessment,
        result.isFinalAssessment,
        result.pastAssessmentId,
      )

      // Determine company status based on remaining assessments
      const remainingAssessments = activeOngoingAssessments?.filter(
        (assessment) => assessment.id !== assessmentId,
      )
      const inProgressCount = remainingAssessments?.filter(
        (assessment) => assessment.status === OngoingAssessmentStatus.IN_PROGRESS,
      ).length
      const readyToStartCount = remainingAssessments?.filter(
        (assessment) => assessment.status === OngoingAssessmentStatus.READY_TO_START,
      ).length

      if (inProgressCount && inProgressCount > 0) {
        // There are still ongoing assessments in progress, keep company in assessment
        // No status change needed
      } else if (readyToStartCount && readyToStartCount > 0) {
        // No more in-progress assessments, but there are ready-to-start assessments
        // Move company to assessment required
        await updateCompany({
          id: companyId,
          status: CompanyStatus.ASSESSMENT_REQUIRED,
        })
      } else {
        // This is the last assessment and no ready-to-start assessments remain
        // Move company to approved
        await updateCompany({
          id: companyId,
          status: CompanyStatus.APPROVED,
          comment: comment || undefined,
        })
      }
    } else if (activeOngoingAssessments?.length && activeOngoingAssessments.length > 0) {
      // In case of no assessmentId, we are finishing all ongoing assessments
      const promises =
        activeOngoingAssessments.map(async (assessment) =>
          finalizeAssessment({
            assessmentId: assessment.id,
            companyId,
            decision,
          }),
        ) || []
      const results = await Promise.all(promises)
      results.forEach(({ hasRejectedAssessment, isFinalAssessment }) => {
        onFinishAssessment?.(hasRejectedAssessment, isFinalAssessment)
      })

      // After finishing all assessments, move company to approved
      await updateCompany({
        id: companyId,
        status: CompanyStatus.APPROVED,
        comment: comment || undefined,
      })
    } else {
      await updateCompany({
        id: companyId,
        status: CompanyStatus.APPROVED,
        comment: comment || undefined,
      })
    }
  }

  const updateNextReAssessmentDueDate = async () => {
    // Skip update if 'existing' option is selected
    if (dueDateOption === 'existing') {
      return
    }

    if (dueDateOption === 'calculated') {
      // Always update to cadence, even if calculatedNextDueDate is undefined
      // Backend will calculate the due date based on cadence
      await updateDueDate({
        orgCompanyId: companyId,
        updateType: ReAssessmentDueDateUpdateType.CADENCE,
      })
    } else if (dueDateOption === 'custom' && customDueDate) {
      const isManualDueDate =
        company?.company?.reAssessmentDueDate?.updateType === ReAssessmentDueDateUpdateType.MANUAL
      trackManualDueDate({
        companyId,
        companyName: company?.company?.profile?.name,
        inherentRiskLevel:
          riskLevelLabel[
            getInherentRisk(company?.company?.validatedRisk, company?.company?.calculatedRisk)
          ],
        newDueDate: formatDate(customDueDate),
        previousDueDate: existingScheduledDueDate ? formatDate(existingScheduledDueDate) : null,
        previousUpdateType: isManualDueDate ? 'MANUAL' : 'CADENCE',
        source: 'finalize-assessment-modal',
      })

      await updateDueDate({
        orgCompanyId: companyId,
        manualDueDate: timestampFromDate(customDueDate),
        updateType: ReAssessmentDueDateUpdateType.MANUAL,
      })
    }
  }

  const handleAssessment = async () => {
    try {
      trackAssessmentCompletion()
      await finalizeAssessmentsAndUpdateStatus()
      await updateNextReAssessmentDueDate()
      onClose()
    } catch (error) {
      reportErrors(error as Error)
      onClose()
      toast({
        variant: 'destructive',
        title: FINISH_ASSESSMENT_MODAL_LABELS.ERROR_TITLE,
        description: FINISH_ASSESSMENT_MODAL_LABELS.ERROR_DESCRIPTION,
      })
    }
  }

  return (
    <Modal
      size='medium'
      open={open}
      onCancel={onClose}
      title={FINISH_ASSESSMENT_MODAL_LABELS.TITLE}
      footer={
        <div className='mt-6 flex w-full items-end justify-between'>
          <Button variant='ghost' onClick={onClose}>
            {FINISH_ASSESSMENT_MODAL_LABELS.CANCEL}
          </Button>
          <div className='flex items-center gap-2'>
            <ExportCompany
              companyId={companyId}
              assessmentId={assessmentId}
              trackingEvent='assessment.export'
              extraTrackingProps={{
                source: ASSESSMENT_EXPORT_SOURCES.FINISH_ASSESSMENT_MODAL,
                assessmentId,
                companyId,
                decision: assessmentDecisionLabel[decision],
                projectId,
                ...extraTrackingProps,
              }}
            />
            <Button
              onClick={handleAssessment}
              disabled={isPending || isUpdatingCompany || isUpdatingDueDate || isCustomDateInvalid}
              className='gap-0.5'
            >
              {(isPending || isUpdatingCompany || isUpdatingDueDate) && (
                <Loader2Icon className='mr-2 size-4 animate-spin' />
              )}
              {FINISH_ASSESSMENT_MODAL_LABELS.TITLE}
            </Button>
          </div>
        </div>
      }
    >
      <h3 className='mb-2 text-main-foreground-primary text-label-default'>
        {FINISH_ASSESSMENT_MODAL_LABELS.ASSESSMENT_CONCLUSION}
      </h3>
      <AssessmentDecisionBadge decision={decision} />
      {hasAlerts && (
        <>
          <h4 className='mb-2 mt-4 text-main-foreground-primary text-label-default'>
            {FINISH_ASSESSMENT_MODAL_LABELS.OPEN_ISSUES}
          </h4>
          <div className='overflow-hidden rounded-md border border-main-border-default shadow-sm'>
            {alerts.map(
              (alert, index) =>
                alert?.condition && (
                  <div
                    key={'id' in alert ? alert.id : index}
                    className='flex items-center gap-1.5 border-b border-main-border-default p-4 last:border-b-0'
                  >
                    <CircleAlertIcon className='size-4 shrink-0 text-main-foreground-danger' />
                    <span className='text-main-foreground-primary text-body-default'>
                      {alert.message}
                    </span>
                  </div>
                ),
            )}
          </div>
        </>
      )}
      <div className='mt-6'>
        <FormControl
          label={FINISH_ASSESSMENT_MODAL_LABELS.DUE_DATE_LABEL}
          helperText={FINISH_ASSESSMENT_MODAL_LABELS.DUE_DATE_HELPER_TEXT}
          errorText={
            isCustomDateInvalid ? FINISH_ASSESSMENT_MODAL_LABELS.DUE_DATE_ERROR : undefined
          }
          state={isCustomDateInvalid ? 'error' : 'default'}
        >
          <RadioGroup
            value={dueDateOption}
            onValueChange={(value) => setDueDateOption(value as DueDateOption)}
            className='flex flex-col gap-0'
          >
            <label className='flex h-8 cursor-pointer select-none items-center gap-2'>
              <RadioGroupItem value='calculated' />
              <span className='text-main-foreground-primary text-body-default'>
                {calculatedNextDueDate
                  ? FINISH_ASSESSMENT_MODAL_LABELS.CALCULATED_NEXT_DUE_DATE(
                      formatDate(calculatedNextDueDate, 'shortDateTime'),
                    )
                  : FINISH_ASSESSMENT_MODAL_LABELS.NO_DUE_DATE}
              </span>
            </label>
            {existingScheduledDueDate && (
              <label className='flex h-8 cursor-pointer select-none items-center gap-2'>
                <RadioGroupItem value='existing' />
                <span className='text-main-foreground-primary text-body-default'>
                  {FINISH_ASSESSMENT_MODAL_LABELS.EXISTING_SCHEDULED_DUE_DATE(
                    formatDate(existingScheduledDueDate, 'shortDateTime'),
                  )}
                </span>
              </label>
            )}
            <label className='flex cursor-pointer select-none items-center gap-2'>
              <RadioGroupItem value='custom' />
              <span className='text-main-foreground-primary text-body-default'>
                {FINISH_ASSESSMENT_MODAL_LABELS.CUSTOM_DUE_DATE}
              </span>
              <DatePicker
                value={customDueDate}
                onChange={setCustomDueDate}
                placeholder=''
                disabled={dueDateOption !== 'custom'}
                emptyStateIcon={<CalendarIcon className='size-4' />}
                className='w-28'
              />
            </label>
          </RadioGroup>
        </FormControl>
      </div>
    </Modal>
  )
}
